// What a job card shows when job/complete refuses an outcome that declares a public ledger message (stage 9). Pure: the
// card base (jobs/job.ts) catches the refusal and asks this which state it is in.

export type JobLedgerGateState =
  // Q7: a company message waits while the signatory list is being changed
  | 'SIGNATORY_CHANGE_PENDING'
  // #38: a company topic message waits while who signs the company's topic is being changed
  | 'TOPIC_SIGNATORY_CHANGE_PENDING'
  // D14, OQ3: the company has paid for credits that haven't all arrived; the card shows them and stays open
  | 'CREDITS_ON_THE_WAY'
  // #25 phase 5 (03 "Refusing and notifying"): a paid action the operating account can't cover. The refusal is recorded and
  // the person is notified to complete the job again once funds arrive; the card stays open. #41: once onboarding is
  // complete, every paid action waits for automatic payments, whatever the balance: a signatory can set them up from My
  // Account (the first paid action sends the set-up card, but never again once the signatories have cancelled one, USER
  // DECISION 2026-10-28)
  | 'AUTO_PAYMENTS_NEEDED'
  // Automatic payments are topping the operating account up
  | 'WAITING_FOR_FUNDS'
  // The last automatic payment failed or waits for the card's owner
  | 'WAITING_FOR_PAYMENT'
  // This month's card or top-up limit is reached
  | 'WAITING_FOR_NEXT_MONTH'
  // No credit price for 7 days, so charges are paused
  | 'WAITING_FOR_PRICE'
  // The £ target buys no more than the threshold at today's price, so charges are stopped
  | 'WAITING_FOR_TARGET_CHANGE'
  // The action needs more than the operating account is topped up to; the signatories have been asked to raise the
  // operating target (the Raise operating target card, #25 phase 6)
  | 'WAITING_FOR_TARGET_RAISE'
  // #25 phase 6 (round 5): the signatories cancelled the Raise operating target card this job was waiting on, so it won't
  // get the credits that way; not a wait (no notice is promised), so the card shows the line as it shows an error
  | 'TARGET_RAISE_DECLINED'
  // D7, OQ6: an individual-topic message from a person with no public ledger account; the card starts (or opens) their
  // create-account workflow, and they complete the job again once the account exists
  | 'NO_LEDGER_ACCOUNT';

export interface JobLedgerGate {
  state: JobLedgerGateState;
  message: string;
}

// The codes job/complete refuses with, from ns-lambda-ts (signing-locks.ts, job-messages.ts, auto-refusals.ts and topics.ts)
export const JOB_LEDGER_GATE_CODES: Record<string, JobLedgerGateState> = {
  PUBLIC_LEDGER_SIGNATORY_CHANGE_PENDING: 'SIGNATORY_CHANGE_PENDING',
  PUBLIC_LEDGER_TOPIC_CHANGE_PENDING: 'TOPIC_SIGNATORY_CHANGE_PENDING',
  PUBLIC_LEDGER_CREDITS_ON_THE_WAY: 'CREDITS_ON_THE_WAY',
  NO_LEDGER_ACCOUNT: 'NO_LEDGER_ACCOUNT',
  PUBLIC_LEDGER_AUTO_PAYMENTS_NEEDED: 'AUTO_PAYMENTS_NEEDED',
  PUBLIC_LEDGER_AUTOPAY_WAITING_FOR_FUNDS: 'WAITING_FOR_FUNDS',
  PUBLIC_LEDGER_AUTOPAY_WAITING_FOR_PAYMENT: 'WAITING_FOR_PAYMENT',
  PUBLIC_LEDGER_AUTOPAY_WAITING_FOR_NEXT_MONTH: 'WAITING_FOR_NEXT_MONTH',
  PUBLIC_LEDGER_AUTOPAY_WAITING_FOR_PRICE: 'WAITING_FOR_PRICE',
  PUBLIC_LEDGER_AUTOPAY_WAITING_FOR_TARGET_CHANGE: 'WAITING_FOR_TARGET_CHANGE',
  PUBLIC_LEDGER_AUTOPAY_WAITING_FOR_TARGET_RAISE: 'WAITING_FOR_TARGET_RAISE',
  PUBLIC_LEDGER_AUTOPAY_TARGET_RAISE_DECLINED: 'TARGET_RAISE_DECLINED',
};

// The waiting states' overlay headings (03): the server's message starts with the same words
export type JobLedgerWaitingState = Extract<
  JobLedgerGateState,
  | 'AUTO_PAYMENTS_NEEDED'
  | 'WAITING_FOR_FUNDS'
  | 'WAITING_FOR_PAYMENT'
  | 'WAITING_FOR_NEXT_MONTH'
  | 'WAITING_FOR_PRICE'
  | 'WAITING_FOR_TARGET_CHANGE'
  | 'WAITING_FOR_TARGET_RAISE'
>;

export const WAITING_TITLES: Record<JobLedgerWaitingState, string> = {
  AUTO_PAYMENTS_NEEDED: 'Waiting for automatic payments',
  WAITING_FOR_FUNDS: 'Waiting for funds',
  WAITING_FOR_PAYMENT: 'Waiting for payment',
  WAITING_FOR_NEXT_MONTH: 'Waiting for next month',
  WAITING_FOR_PRICE: 'Waiting for a price',
  WAITING_FOR_TARGET_CHANGE: 'Waiting for the target to be changed',
  WAITING_FOR_TARGET_RAISE: 'Waiting for the target to be raised',
};

export const NOTIFIED_LATER = "We'll let you know when you can complete it again.";

// Used when the refusal carries no message of its own. They mirror the server's wording by hand (ns-lambda-ts
// src/_core/public-ledger/auto-refusals.ts REFUSALS): change both together
const FALLBACK_MESSAGES: Record<JobLedgerGateState, string> = {
  SIGNATORY_CHANGE_PENDING: 'Please wait: the signatory list is being changed',
  TOPIC_SIGNATORY_CHANGE_PENDING: "Please wait: who signs the company's topic messages is being changed",
  // #25 phase 5: recorded like the waiting states, so it promises the notice too
  CREDITS_ON_THE_WAY: `Some of your company's nodeStream credits are still on their way. ${NOTIFIED_LATER}`,
  NO_LEDGER_ACCOUNT: 'You need a public ledger account to sign this message',
  // #41: every paid action waits for automatic payments once onboarding is complete, whatever the balance (DECISION 6)
  AUTO_PAYMENTS_NEEDED: `Waiting for automatic payments. Your company needs automatic payments on for public ledger actions. A signatory can set them up from My Account. ${NOTIFIED_LATER}`,
  WAITING_FOR_FUNDS: `Waiting for funds. Your company's automatic payments are topping up its public ledger account. ${NOTIFIED_LATER}`,
  WAITING_FOR_PAYMENT: `Waiting for payment. Your company's last automatic payment didn't go through, and its signatories have been asked to sort it out. ${NOTIFIED_LATER}`,
  WAITING_FOR_NEXT_MONTH: `Waiting for next month. Your company's automatic payments have reached their monthly limit. ${NOTIFIED_LATER}`,
  WAITING_FOR_PRICE: `Waiting for a price. nodeStream can't get a current credit price, so your company's automatic payments are paused. ${NOTIFIED_LATER}`,
  WAITING_FOR_TARGET_CHANGE: `Waiting for the target to be changed. At today's price your company's automatic payments target no longer buys enough credits, and its signatories have been asked to change it. ${NOTIFIED_LATER}`,
  WAITING_FOR_TARGET_RAISE: `Waiting for the target to be raised. This needs more credits than your company's operating account is topped up to, and its signatories have been asked to raise it. ${NOTIFIED_LATER}`,
  // Approved (round 4 follow-ups)
  TARGET_RAISE_DECLINED:
    "Your company's signatories decided not to raise the automatic payments operating target, so this job can't get the credits it needs. You'll need another way to complete it.",
};

/**
 * The card state for a refused completion, or null when the refusal isn't one of the public ledger checks (the card then
 * shows the error as it does any other).
 */
export function jobLedgerGate(code: string | null | undefined, message?: string | null): JobLedgerGate | null {
  const state = code ? JOB_LEDGER_GATE_CODES[code] : undefined;
  if (!state) return null;
  return { state, message: message && message.trim() ? message : FALLBACK_MESSAGES[state] };
}

export interface WaitingOverlay {
  title: string;
  paragraphs: string[];
  buttonText: string;
}

function isWaiting(state: JobLedgerGateState): state is JobLedgerWaitingState {
  return state in WAITING_TITLES;
}

/**
 * The overlay for a refusal the person is notified about (#25 phase 5), or null for any other gate state. The heading is the
 * "Waiting for …" wording; the paragraphs are the refusal's message without that opening sentence, then the promise that
 * they'll be told when they can complete it again. The job stays open: nothing completes by itself (DECISION 3.2).
 */
export function waitingOverlay(gate: JobLedgerGate): WaitingOverlay | null {
  if (!isWaiting(gate.state)) return null;
  const title = WAITING_TITLES[gate.state];
  let body = gate.message.trim();
  if (body.toLowerCase().startsWith(`${title.toLowerCase()}.`)) body = body.slice(title.length + 1).trim();
  const notified = body.endsWith(NOTIFIED_LATER);
  if (notified) body = body.slice(0, -NOTIFIED_LATER.length).trim();
  return {
    title,
    paragraphs: [...(body ? [body] : []), NOTIFIED_LATER],
    buttonText: 'I understand',
  };
}

/**
 * Whether a refused job/complete is shown as the waiting overlay (#25 phase 5): the card says why and that they'll be
 * notified, so no error toast goes with it. Any other refusal or error is still toasted.
 */
export function shownAsWaitingOverlay(code: string | null | undefined, message?: string | null): boolean {
  const gate = jobLedgerGate(code, message);
  return gate !== null && waitingOverlay(gate) !== null;
}

// What public-ledger-account-launch answers: the person's create-account workflow, and whether it was already open
export interface AccountLaunch {
  workflowId: string;
  existing: boolean;
}

/** The account-launch answer, or null when it doesn't name a workflow. */
export function accountLaunchResult(res: unknown): AccountLaunch | null {
  if (typeof res !== 'object' || res === null) return null;
  const body = res as Record<string, unknown>;
  if (typeof body.workflowId !== 'string' || !body.workflowId) return null;
  return { workflowId: body.workflowId, existing: body.existing === true };
}

export interface NoAccountOverlay {
  title: string;
  paragraphs: string[];
  buttonText: string;
  // Where the button goes: the create-account workflow
  buttonPath: string;
}

/**
 * The overlay for NO_LEDGER_ACCOUNT once the create-account workflow is started or found: the refusal's message, what
 * happened, and the way back. Nothing completes by itself: the person completes this job again afterwards (OQ6).
 */
export function noAccountOverlay(gate: JobLedgerGate, launch: AccountLaunch): NoAccountOverlay {
  return {
    title: 'Create your public ledger account first',
    paragraphs: [
      gate.message,
      launch.existing
        ? 'Your "Create a public ledger account" workflow is already open.'
        : 'We\'ve started a "Create a public ledger account" workflow for you.',
      'Finish it, then come back and complete this job again.',
    ],
    buttonText: 'Open it',
    buttonPath: `/workflows/${launch.workflowId}`,
  };
}
